import { useMemo, useRef, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useClientStore } from '../../lib/stores/clientStore';
import { useApp } from '../AppProvider';
import { supabase } from '../../lib/supabase';
import {
  validateClientForContract,
  type RequiredClientField,
} from '../../lib/utils/validateClientForContract';
import { isRecognizedForeignCountry } from '../../lib/utils/countries';
import { saveGroupNumbersAsContacts } from '../../lib/utils/saveGroupNumbersAsContacts';
import { FicClientLookup, type FicMatch } from './FicClientLookup';
import { DrawerShell } from './acceptance-drawer/DrawerShell';
import { AcceptanceChecklist, type ChecklistItem } from './acceptance-drawer/AcceptanceChecklist';
import { ClientDataRows } from './acceptance-drawer/ClientDataRows';
import { WhatsappGroupFields } from './acceptance-drawer/WhatsappGroupFields';

interface ClientRequiredFieldsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  /** Valori attuali del cliente (prefill). */
  client: Record<string, any> | null;
  /** Campi obbligatori configurati (già al netto degli esoneri per paese). */
  requiredFields: RequiredClientField[];
  /** Paese del cliente: se estero riconosciuto mostra il callout di esonero SDI/PEC/CF. */
  clientCountry?: string | null;
  /** Chiamato dopo salvataggio con dati validi → il chiamante riprende l'azione. */
  onSaved: () => void;
  /** ID del deal (necessario per il token whatsapp_group). */
  dealId?: string;
  /** Numeri WhatsApp già salvati sul deal (prefill). */
  dealGroupNumbers?: string[];
  /** Nome gruppo già salvato sul deal (prefill). */
  dealGroupName?: string;
  /** Nome del cliente (suggerisce il nome gruppo default). */
  clientName?: string;
}

/**
 * Gate dati cliente per contratto/firma in versione drawer (Parte C anagrafica
 * internazionale): checklist di stato + sezione Dati cliente a righe con
 * ricerca Fatture in Cloud + campi gruppo WhatsApp quando richiesti.
 * Sostituisce ClientRequiredFieldsModal con la stessa logica di salvataggio.
 */
export function ClientRequiredFieldsDrawer({
  isOpen,
  onClose,
  clientId,
  client,
  requiredFields,
  clientCountry,
  onSaved,
  dealId,
  dealGroupNumbers,
  dealGroupName,
  clientName,
}: ClientRequiredFieldsDrawerProps) {
  const { updateClient } = useClientStore();
  const { toast } = useApp();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, string>>(() => ({
    business_name: client?.business_name ?? '',
    vat_number: client?.vat_number ?? '',
    tax_code: client?.tax_code ?? '',
    sdi_code: client?.sdi_code ?? '',
    pec: client?.pec ?? '',
    email: client?.email ?? '',
    email_amministrazione: client?.email_amministrazione ?? '',
    street_address: client?.street_address ?? '',
    street_number: client?.street_number ?? '',
    postal_code: client?.postal_code ?? '',
    city: client?.city ?? '',
    province: client?.province ?? '',
  }));
  const [showErrors, setShowErrors] = useState(false);
  const waRef = useRef<HTMLDivElement>(null);

  // WhatsApp group state (deal-level fields)
  const defaultGroupName = dealGroupName ?? (clientName ? `${clientName} <> Obly` : '');
  const [groupNumbers, setGroupNumbers] = useState<string[]>(
    () => (dealGroupNumbers && dealGroupNumbers.length > 0 ? dealGroupNumbers : ['']),
  );
  const [groupName, setGroupName] = useState<string>(defaultGroupName);
  // '' = crea un nuovo gruppo; JID = riusa un gruppo esistente del cliente.
  const [reuseGroupJid, setReuseGroupJid] = useState<string>('');

  // Autofill dal match FIC: compila SOLO i campi vuoti, mai sovrascrivere.
  // N° civico resta vuoto: FIC non lo separa dalla via.
  const applyFicMatch = (m: FicMatch) => {
    const candidates: Record<string, string | null> = {
      business_name: m.name,
      vat_number: m.vat_number,
      tax_code: m.tax_code,
      sdi_code: m.ei_code,
      pec: m.certified_email,
      email_amministrazione: m.email,
      street_address: m.address_street,
      postal_code: m.address_postal_code,
      city: m.address_city,
      province: m.address_province,
    };
    const filled = Object.entries(candidates)
      .filter(([key, value]) => value && !(form[key] ?? '').trim())
      .map(([key]) => key);
    setForm((prev) => {
      const next = { ...prev };
      for (const [key, value] of Object.entries(candidates)) {
        if (value && !(prev[key] ?? '').trim()) next[key] = value;
      }
      return next;
    });
    toast.success(
      filled.length > 0
        ? `${filled.length} campi compilati da Fatture in Cloud`
        : 'Nessun campo da compilare: erano già tutti valorizzati',
    );
  };

  // Record di validazione: form cliente + dati gruppo WhatsApp (a livello deal)
  const validationRecord = useMemo(
    () => ({
      ...form,
      whatsapp_group_numbers: groupNumbers.filter((n) => n.trim() !== ''),
      whatsapp_group_name: groupName,
      // Riusando un gruppo esistente il validatore bypassa numeri e nome.
      whatsapp_reuse_group_jid: reuseGroupJid || null,
    }),
    [form, groupNumbers, groupName, reuseGroupJid],
  );

  const validation = useMemo(
    () => validateClientForContract(validationRecord, requiredFields),
    [validationRecord, requiredFields],
  );

  const showWhatsappGroup = requiredFields.includes('whatsapp_group');
  const clientMissing = validation.missingFields.filter((f) => f !== 'whatsapp_group');
  const waMissing = validation.missingFields.includes('whatsapp_group');

  const handleSave = async () => {
    if (!validation.isValid) {
      setShowErrors(true);
      return;
    }
    setSaving(true);
    try {
      await updateClient(clientId, {
        business_name: form.business_name || undefined,
        vat_number: form.vat_number || null,
        tax_code: form.tax_code || null,
        sdi_code: form.sdi_code || null,
        pec: form.pec || null,
        email: form.email || null,
        email_amministrazione: form.email_amministrazione || null,
        street_address: form.street_address || null,
        street_number: form.street_number || null,
        postal_code: form.postal_code || null,
        city: form.city || null,
        province: form.province || null,
      } as any);

      // Persiste i dati WhatsApp group sul deal (se richiesto)
      if (showWhatsappGroup && dealId) {
        const cleanedNumbers = groupNumbers.map((n) => n.trim()).filter((n) => n !== '');
        const { error: dealError } = await supabase
          .from('deals')
          .update({
            whatsapp_group_numbers: cleanedNumbers,
            whatsapp_group_name: groupName.trim(),
            // Gruppo esistente scelto per il riuso (null = crea nuovo gruppo).
            whatsapp_reuse_group_jid: reuseGroupJid || null,
          })
          .eq('id', dealId);
        if (dealError) throw dealError;

        // Salva i numeri anche come contatti del cliente (best-effort, dedup su telefono).
        await saveGroupNumbersAsContacts(clientId, cleanedNumbers);
      }

      toast.success('Dati cliente aggiornati');
      onSaved();
    } catch (e) {
      console.error('save failed:', e);
      toast.error('Errore nel salvataggio dei dati cliente');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const checklist: ChecklistItem[] = [
    {
      key: 'client',
      label: 'Dati cliente',
      ok: clientMissing.length === 0,
      summary: clientMissing.length === 0 ? 'completi' : `${clientMissing.length} ${clientMissing.length === 1 ? 'campo mancante' : 'campi mancanti'}`,
      error: showErrors && clientMissing.length > 0,
    },
    ...(showWhatsappGroup ? [{
      key: 'whatsapp',
      label: 'Gruppo WhatsApp',
      ok: !waMissing,
      summary: reuseGroupJid ? 'gruppo esistente' : groupName.trim() || 'da configurare',
      error: showErrors && waMissing,
      onClick: () => waRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    }] : []),
  ];

  return (
    <DrawerShell
      kicker="Verifica dati cliente"
      onClose={onClose}
      // Il gate si apre anche sopra i modali offerta (z 20000).
      zClass="z-[21000]"
      escapeLayer={21000}
      footer={
        <>
          <span className="flex-1" />
          <button type="button" onClick={onClose} disabled={saving}
            className="rounded px-3 py-1.5 text-[12.5px] font-medium text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] disabled:opacity-50">
            Annulla
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded bg-[#37352F] px-3.5 py-1.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50 dark:bg-white dark:text-[#191919]">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Conferma e continua
          </button>
        </>
      }
    >
      <div className="mt-1 text-[17px] font-semibold text-[#37352F] dark:text-white truncate">
        {clientName || form.business_name || 'Cliente'}
      </div>

      <AcceptanceChecklist items={checklist} />

      <div className="mt-3 flex items-start gap-2 rounded-md bg-[#FFF8E1] dark:bg-amber-900/20 border border-[#FFE0A3] dark:border-amber-800 px-3 py-2.5">
        <AlertCircle className="w-4 h-4 text-[#B7791F] mt-0.5 flex-shrink-0" />
        <p className="text-[12px] text-[#8A6D3B] dark:text-amber-200">
          Verifica che i dati del cliente siano corretti prima di procedere con
          contratto/firma. Eventuali modifiche verranno salvate nella scheda cliente.
        </p>
      </div>

      {isRecognizedForeignCountry(clientCountry) && (
        <div className="mt-2 flex items-start gap-2 rounded-md bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.06] dark:border-white/[0.08] px-3 py-2.5">
          <AlertCircle className="w-4 h-4 text-[#787774] dark:text-gray-400 mt-0.5 flex-shrink-0" />
          <p className="text-[12px] text-[#6B6762] dark:text-gray-300">
            Cliente estero ({clientCountry}): Codice SDI, PEC e Codice Fiscale non sono
            richiesti per questo cliente.
          </p>
        </div>
      )}

      <div className="mt-3">
        <FicClientLookup
          defaultQuery={form.business_name || clientName || ''}
          onPick={applyFicMatch}
        />
      </div>

      <div className="mt-2">
        <ClientDataRows
          values={form}
          onChange={(field, value) => setForm((p) => ({ ...p, [field]: value }))}
          onChangeMany={(fields) => setForm((p) => ({ ...p, ...fields }))}
          requiredFields={requiredFields}
          showErrors={showErrors}
          missingFields={validation.missingFields}
          country={clientCountry}
        />
      </div>

      {showWhatsappGroup && (
        <div className="mt-3" ref={waRef}>
          <WhatsappGroupFields
            clientId={clientId}
            groupNumbers={groupNumbers}
            onNumbersChange={setGroupNumbers}
            groupName={groupName}
            onGroupName={setGroupName}
            reuseGroupJid={reuseGroupJid}
            onReuseGroupJid={setReuseGroupJid}
            clientName={clientName}
            showMissing={showErrors && waMissing}
          />
        </div>
      )}
    </DrawerShell>
  );
}
